import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';
import { isolatedCloudDomain } from '@atlaskit/atlassian-context/isolated-cloud-domain';
import type { ProductEnvironment } from '@atlassian/forge-ui-types/environments';

import { getForgeCDNBaseDomain } from './getForgeCDNBaseDomain';

/*
	UUIDs are in the format of 32 hexadecimal characters with four hyphens:
	XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX.

	If we intend to concatenate two shortened UUIDs in a subdomain label in the format
	${shortenedInstallationId}--${shortenedAppId}, where the max length of the label is
	63 characters (https://www.rfc-editor.org/rfc/rfc2181), then each id can have a max
	length of 30 characters. An underscore cannot be used as the delimiter because
	underscores are not allowed in domains. A single hyphen cannot be used because the
	delimiter needs to be unique and the ids themselves contain hyphens. Hence, the
	delimiter is a double-hyphen.

	The "highest" UUID is ffffffff-ffff-ffff-ffff-ffffffffffff. The base-36 string encoding
	(a-z, 0-9) is 1z141z3-1ekf-1ekf-1ekf-2rrvthnxtr, which has a length of 33. Too long.

	Combining the middle three segments in the UUID, i.e. ffffffff-ffffffffffff-ffffffffffff, and
	then encoding gives 1z141z3-2rrvthnxtr-2rrvthnxtr, which has a length of 29. This is what
	we will do.

	Precision should not be lost when decoding back to hexadecimal, because the longest segment
	in a UUID is 12 characters, while Number.MAX_SAFE_INTEGER.toString(16).length === 14.
*/
export function shortenUuid(uuid: string): string {
	const [a, b, c, d, e] = uuid.split('-');
	const combinedUuid = [a, `${b}${c}${d}`, e];

	return combinedUuid
		.map((s) => parseInt(s, 16))
		.map((s) => s.toString(36))
		.join('-');
}

/**
 * The DNS label identifying one app installation's bundle on the Forge CDN, i.e. everything before
 * the base domain that {@link getForgeCDNBaseDomain} returns.
 *
 * Identical in every perimeter - only the base domain differs.
 *
 * The appId is included alongside the installationId so that it can be compared against the appId
 * in the resource path. That comparison is performed by the routing Lambda@Edge to prevent a
 * malicious developer from appending their malicious app path to a legitimate installation id.
 */
export function expandUuid(shortenedUuid: string): string {
	const [a, b, c] = shortenedUuid
		.split('-')
		.map((s) => parseInt(s, 36))
		.map((s) => s.toString(16));

	// expands the middle segment into the original UUID
	// ffffffff-abcdabcdabcd-ffffffffffff ===> ffffffff-abcd-abcd-abcd-ffffffffffff
	const middleExpanded = b.match(/.{1,4}/g) || b;
	return [a, ...middleExpanded, c].join('-');
}

export function getForgeAppCdnSubdomain(installationId: string, appId: string): string {
	return `${shortenUuid(installationId)}--${shortenUuid(appId)}`;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The origin a Forge app's bundle is served from.
 *
 * Only the origin is derivable here. The rest of the iframe URL ends in
 * `_ctx_<base64(gzip(appContext))>`, and `getEncodedAppContext` reads `document.referrer` and
 * `window.location`.
 *
 */
export function getForgeAppCdnOrigin({
	appId,
	environment,
	installationId,
	options,
}: {
	appId: string | undefined;
	environment: ProductEnvironment;
	installationId: string | undefined;
	options?: {
		// SSR passes the isolated cloud domain here
		isolatedCloudDomain?: string;
	};
}): string | undefined {
	if (!installationId || !appId) {
		return undefined;
	}

	// `shortenUuid` would turn anything else into `NaN` segments and a bogus host.
	if (!UUID_PATTERN.test(installationId) || !UUID_PATTERN.test(appId)) {
		return undefined;
	}

	const subdomain = getForgeAppCdnSubdomain(installationId, appId);

	if (isIsolatedCloud()) {
		const icDomain = options?.isolatedCloudDomain ?? isolatedCloudDomain();

		return icDomain ? `https://${subdomain}.forge-cdn.${icDomain}` : undefined;
	}

	return `https://${subdomain}.${getForgeCDNBaseDomain(environment)}`;
}
